import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard,
  Ticket,
  Users,
  Settings,
  LogOut,
  Kanban,
  Menu,
  X,
  ListTodo,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useState } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { name: 'Kanban Board', href: '/admin/kanban', icon: Kanban },
  { name: 'All Tickets', href: '/admin/tickets', icon: Ticket },
  { name: 'Users', href: '/admin/users', icon: Users, adminOnly: true, teamLeadOnly: true },
  { name: 'Tasks', href: '/admin/tasks', icon: ListTodo, teamLeadOnly: true, agentOnly: true },
  { name: 'Requests', href: '/admin/requests', icon: MessageSquare, teamLeadOnly: true, agentOnly: true },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
];

export function AdminLayout() {
  const { user, isAdmin, isTeamLeader, isAgent, signOut } = useAuth();
  const profile = user?.profile;
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const roleAwareNavigation = navigation
    .map((item) => {
      if (item.href === '/admin/kanban' && (isTeamLeader || isAgent)) {
        return { ...item, name: 'Tasks' };
      }
      return item;
    })
    .filter((item) => {
      if (item.href === '/admin/tasks' && (isTeamLeader || isAgent)) return false;
      return true;
    });

  const filteredNav = roleAwareNavigation.filter((item) => {
    const adminOnly = (item as { adminOnly?: boolean }).adminOnly;
    const teamLeadOnly = (item as { teamLeadOnly?: boolean }).teamLeadOnly;
    const agentOnly = (item as { agentOnly?: boolean }).agentOnly;
    if (adminOnly && teamLeadOnly) return isAdmin || isTeamLeader;
    if (adminOnly) return isAdmin;
    if (teamLeadOnly && agentOnly) return isTeamLeader || isAgent; // Tasks: show for team leader or agent
    if (teamLeadOnly) return isTeamLeader;
    if (agentOnly) return isAgent;
    return true;
  });

  return (
    <div className="flex min-h-screen">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-sidebar transition-transform duration-200 lg:translate-x-0 lg:static',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
            <Link to="/admin" className="flex items-center gap-3">
              <img
                src="/img/primary-logo-with-text.svg"
                alt="Tickettrax"
                className="h-12 w-auto"
              />
            </Link>
            <button
              className="lg:hidden text-sidebar-foreground"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {filteredNav.map(item => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-sidebar-border p-4">
            <Link
              to="/admin/settings"
              className="flex items-center gap-3 mb-3 rounded-lg p-1 -m-1 hover:bg-sidebar-accent/50 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={(profile as { avatarUrl?: string; avatar_url?: string })?.avatarUrl ?? (profile as { avatar_url?: string })?.avatar_url ?? undefined} />
                <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground">
                  {profile?.name?.charAt(0) ?? 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {profile?.name ?? 'User'}
                </p>
                <p className="text-xs text-sidebar-foreground/60 truncate">
                  {isAdmin ? 'Admin' : isTeamLeader ? 'Team Lead' : 'Agent'}
                </p>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              onClick={handleSignOut}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 lg:px-6">
          <button
            className="lg:hidden text-foreground"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-background p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
